<template>
  <div class="row mt-2">
    <v-input
      input-id="account-owner"
      label="Titulaire du compte"
      getter-name="accountOwner"
      :module-name="moduleName"
      path="accountOwner"
      placeholder="Nicolas Moreau"
    />
    <div class="text-start d-flex flex-column mt-auto">
      <label
        for="gocardless-iban"
        class="text-secondary my-2"
      > {{ label }} </label>
      <input
        id="gocardless-iban"
        v-model="iban"
        type="text"
        class="form-control"
        :class="{
          'is-valid': isValidIban && iban.length > 0,
          'is-invalid': validationError && iban.length > 0
        }"
        placeholder="FR76 1234 1234 1234 1234 1234 123"
        @input="validateIban"
      >
    </div>
    <div
      v-if="isDescriptionVisible"
      class="mt-1"
    >
      <small style="font-style: italic; font-weight: bold;">
        En enregistrant ce RIB, vous autorisez Assurup et GoCardless à envoyer
        des instructions à votre banque pour débiter votre compte.
      </small>
    </div>
    <div class="col-12">
      <transition
        tag="div"
        @enter="tf_slideDown"
        @leave="tf_slideUp"
      >
        <div
          v-if="validationError"
          id="iban-error"
          class="animated"
        >
          <small
            role="alert"
            class="text-danger"
            v-text="validationError"
          />
        </div>
      </transition>
    </div>
    <div class="col-12">
      <transition
        tag="div"
        @enter="tf_slideDown"
        @leave="tf_slideUp"
      >
        <div
          v-if="error !== null"
          class="col-12 mt-2"
        >
          <div class="text-danger text-center">
            {{ error }}
          </div>
        </div>
      </transition>
      <v-loader
        id="gocardless_submit"
        :next="payment"
        :title="isSetupMode ? 'Ajouter mon IBAN' : `Payer ${format(price)} €`"
        icon="arrow-white-right"
        class="my-3 w-100"
        :disabled="!isValidIban"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { transform } from '@/mixins';
import { formatPrice } from '@/utils/_Number';
import Input from '@/components/common/input/Input2.vue';

export default {
  name: 'GoCardlessIban',
  components: {
    'v-input': Input,
  },
  mixins: [transform],
  props: {
    price: {
      type: Number,
      required: false,
      default: 0,
    },
    isSetupMode: {
      type: Boolean,
      default: false,
    },
    apiScope: {
      type: String,
      default: 'checkout',
    },
    moduleName: {
      type: String,
      default: 'checkout',
    },
    isDescriptionVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['payment:success', 'form:error'],
  data() {
    return {
      label: 'IBAN',
      iban: '',
      validationError: '',
      error: null,
      isValidIban: false,
    };
  },
  computed: {
    ...mapGetters({
      transaction: 'checkout/transaction',
      currentCompany: 'risk',
    }),
  },
  methods: {
    validateIban() {
      const cleanIban = this.iban.replace(/\s/g, '').toUpperCase();

      if (cleanIban.length === 0) {
        this.validationError = '';
        this.isValidIban = false;
        return;
      }

      if (cleanIban.length < 15 || cleanIban.length > 34) {
        this.validationError = 'L\'IBAN doit contenir entre 15 et 34 caractères';
        this.isValidIban = false;
        return;
      }

      if (!/^[A-Z]{2}\d{2}/.test(cleanIban)) {
        this.validationError = 'L\'IBAN doit commencer par le code pays (2 lettres) suivi de 2 chiffres';
        this.isValidIban = false;
        return;
      }

      if (!this.validateIbanChecksum(cleanIban)) {
        this.validationError = 'L\'IBAN saisi n\'est pas valide';
        this.isValidIban = false;
        return;
      }

      this.validationError = '';
      this.isValidIban = true;
    },
    validateIbanChecksum(iban) {
      const rearranged = iban.substring(4) + iban.substring(0, 4);

      let numericString = '';
      for (let i = 0; i < rearranged.length; i++) {
        const char = rearranged[i];
        if (/[A-Z]/.test(char)) {
          numericString += (char.charCodeAt(0) - 55).toString();
        } else {
          numericString += char;
        }
      }

      return this.calculateMod97(numericString) === 1;
    },
    calculateMod97(numericString) {
      let remainder = 0;
      for (let i = 0; i < numericString.length; i++) {
        remainder = (remainder * 10 + parseInt(numericString[i], 10)) % 97;
      }
      return remainder;
    },
    async payment() {
      try {
        const bankAccountPayload = {
          iban: this.iban.replace(/\s/g, '').toUpperCase(),
          active: true,
          organization: this.currentCompany,
        };

        if (this.isSetupMode || this.apiScope === 'payments') {
          await this.$store.dispatch('payments/createGoCardlessBankAccount', bankAccountPayload);
          if (this.apiScope === 'payments') {
            await this.$store.dispatch('payments/getGoCardlessBankAccounts');
          }
          this.$emit('payment:success');
          return;
        }
        await this.$store.dispatch('checkout/createGoCardlessBankAccount', bankAccountPayload);

        await this.$store.dispatch('checkout/paymentGoCardless', {
          transactionId: this.transaction.id,
          iban: this.iban.replace(/\s/g, '').toUpperCase(),
        });

        this.$store.dispatch('analytics/purchase');
        this.$emit('payment:success');
      } catch (err) {
        this.error = 'Erreur, veuillez réessayer ultérieurement';
        this.$emit('form:error', err);
      }
    },
    format: (price) => formatPrice(price, true),
  },
};
</script>
